import ACTION_TYPES from '../../constants/actionTypes';
import find from 'lodash/find';
import api from '../../api';

/**
 * Create a quote
 * =========================
 */

function createQuoteRequest(opts) {
  return {
    type: ACTION_TYPES.CREATE_QUOTE,
    payload: opts,
  };
}

function createQuoteSuccess(quote) {
  return {
    type: ACTION_TYPES.CREATE_QUOTE_SUCCESS,
    payload: quote,
  };
}

function createQuoteFailure(error) {
  return {
    type: ACTION_TYPES.CREATE_QUOTE_FAILURE,
    error,
  };
}

export function createQuote(opts) {
  return (dispatch, getState) => {
    const { authToken, broker, clients } = getState();
    let payload;
    if (opts.clientId) {
      const client = find(clients, { id: opts.clientId });
      payload = {
        authToken,
        broker,
        verticalCode: opts.verticalCode,
        client,
      };
    } else {
      payload = {
        ...opts,
      };
    }

    dispatch(createQuoteRequest(opts));
    return new Promise((resolve, reject) => {
      api
        .createQuote(payload)
        .then((quote) => {
          dispatch(createQuoteSuccess(quote));
          resolve(quote);
        })
        .catch((error) => {
          dispatch(createQuoteFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Update a quote
 * =========================
 */

function updateQuoteRequest(opts) {
  return {
    type: ACTION_TYPES.UPDATE_QUOTE,
    payload: opts,
  };
}

function updateQuoteSuccess(quote) {
  return {
    type: ACTION_TYPES.UPDATE_QUOTE_SUCCESS,
    payload: quote,
  };
}

function updateQuoteFailure(error) {
  return {
    type: ACTION_TYPES.UPDATE_QUOTE_FAILURE,
    error,
  };
}

export function updateQuote(quote) {
  return (dispatch, getState) => {
    const { authToken } = getState();
    const payload = {
      authToken,
      quote,
    };

    dispatch(updateQuoteRequest(quote));

    switch (quote.vertical_code) {
      case 'chubb_boa':
        return new Promise((resolve, reject) => {
          api
            .updateBOAQuote(payload)
            .then((quote) => {
              dispatch(updateQuoteSuccess(quote));
              resolve(quote);
            })
            .catch((error) => {
              dispatch(updateQuoteFailure(error));
              reject(error);
            });
        });
      case 'chubb_passport360':
      default:
        return new Promise((resolve, reject) => {
          api
            .updatePassport360Quote(payload)
            .then((quote) => {
              dispatch(updateQuoteSuccess(quote));
              resolve(quote);
            })
            .catch((error) => {
              dispatch(updateQuoteFailure(error));
              reject(error);
            });
        });
    }
  };
}

/**
 * Get quote
 * =========================
 */

function getQuoteRequest(quoteId) {
  return {
    type: ACTION_TYPES.GET_QUOTE,
    payload: {
      quoteId,
    },
  };
}

function getQuoteSuccess(quote) {
  return {
    type: ACTION_TYPES.GET_QUOTE_SUCCESS,
    payload: quote,
  };
}

function getQuoteFailure(error) {
  return {
    type: ACTION_TYPES.GET_QUOTE_FAILURE,
    error,
  };
}

export function getQuote(quoteId) {
  return (dispatch, getState) => {
    const { authToken } = getState();
    const payload = {
      authToken,
      quoteId,
    };

    dispatch(getQuoteRequest(quoteId));
    return new Promise((resolve, reject) => {
      api
        .getQuote(payload)
        .then((quote) => {
          dispatch(getQuoteSuccess(quote));
          resolve(quote);
        })
        .catch((error) => {
          dispatch(getQuoteFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Get quotes
 * =========================
 */

function getQuotesRequest(opts) {
  return {
    type: ACTION_TYPES.GET_QUOTES,
    payload: opts,
  };
}

function getQuotesSuccess(quotes) {
  return {
    type: ACTION_TYPES.GET_QUOTES_SUCCESS,
    payload: quotes,
  };
}

function getQuotesFailure(error) {
  return {
    type: ACTION_TYPES.GET_QUOTES_FAILURE,
    error,
  };
}

export function getQuotes({ clientId }) {
  return (dispatch, getState) => {
    const { authToken } = getState();
    const payload = {
      authToken,
      clientId,
    };

    dispatch(getQuotesRequest({ clientId }));
    return new Promise((resolve, reject) => {
      api
        .getQuotes(payload)
        .then((quotes) => {
          dispatch(getQuotesSuccess(quotes));
          resolve(quotes);
        })
        .catch((error) => {
          dispatch(getQuotesFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Handoff a quote
 * =========================
 */

function handoffQuoteRequest(opts) {
  return {
    type: ACTION_TYPES.HANDOFF_QUOTE,
    payload: opts,
  };
}

function handoffQuoteSuccess(response) {
  return {
    type: ACTION_TYPES.HANDOFF_QUOTE_SUCCESS,
    payload: response,
  };
}

function handoffQuoteFailure(error) {
  return {
    type: ACTION_TYPES.HANDOFF_QUOTE_FAILURE,
    error,
  };
}

export function handoffQuote({ quoteNumber, handoffType }) {
  return (dispatch, getState) => {
    const { authToken } = getState();
    const payload = {
      authToken,
      quoteNumber,
      handoffType,
    };

    dispatch(handoffQuoteRequest({ quoteNumber, handoffType }));
    return new Promise((resolve, reject) => {
      api
        .handoffQuote(payload)
        .then((response) => {
          dispatch(handoffQuoteSuccess(response));
          resolve(response);
        })
        .catch((error) => {
          dispatch(handoffQuoteFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Get plans
 * =========================
 */

function getPlansRequest(quoteId) {
  return {
    type: ACTION_TYPES.GET_PLANS,
    payload: {
      quoteId,
    },
  };
}

function getPlansSuccess(resp) {
  return {
    type: ACTION_TYPES.GET_PLANS_SUCCESS,
    payload: resp,
  };
}

function getPlansFailure(error) {
  return {
    type: ACTION_TYPES.GET_PLANS_FAILURE,
    error,
  };
}

export function getPlans(quoteId) {
  return (dispatch, getState) => {
    const { authToken } = getState();
    const payload = {
      authToken,
      quoteId,
    };

    dispatch(getPlansRequest(quoteId));
    return new Promise((resolve, reject) => {
      api
        .getPlans(payload)
        .then((resp) => {
          dispatch(getPlansSuccess(resp));
          resolve(resp);
        })
        .catch((error) => {
          dispatch(getPlansFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Update plans filter
 * =========================
 */

export function updatePlansFilter(filter = {}) {
  return {
    type: ACTION_TYPES.UPDATE_PLANS_FILTER,
    payload: filter,
  };
}

/**
 * Checkout a quote
 * =========================
 */

function checkoutQuoteRequest(quoteId) {
  return {
    type: ACTION_TYPES.CHECKOUT_QUOTE,
    payload: {
      quoteId,
    },
  };
}

function checkoutQuoteSuccess(checkout) {
  return {
    type: ACTION_TYPES.CHECKOUT_QUOTE_SUCCESS,
    payload: checkout,
  };
}

function checkoutQuoteFailure(error) {
  return {
    type: ACTION_TYPES.CHECKOUT_QUOTE_FAILURE,
    error,
  };
}

export function checkoutQuote(quoteId) {
  return (dispatch, getState) => {
    const { authToken } = getState();
    const payload = {
      authToken,
      quoteId,
    };

    dispatch(checkoutQuoteRequest(quoteId));
    return new Promise((resolve, reject) => {
      api
        .checkoutQuote(payload)
        .then((checkout) => {
          dispatch(checkoutQuoteSuccess(checkout));
          resolve(checkout);
        })
        .catch((error) => {
          dispatch(checkoutQuoteFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Finalize a quote
 * =========================
 */

function finalizeQuoteRequest(quoteId) {
  return {
    type: ACTION_TYPES.FINALIZE_QUOTE,
    payload: {
      quoteId,
    },
  };
}

function finalizeQuoteSuccess(finalize) {
  return {
    type: ACTION_TYPES.FINALIZE_QUOTE_SUCCESS,
    payload: finalize,
  };
}

function finalizeQuoteFailure(error) {
  return {
    type: ACTION_TYPES.FINALIZE_QUOTE_FAILURE,
    error,
  };
}

export function finalizeQuote({ quoteId, deleteAuthHeader = false }) {
  return (dispatch, getState) => {
    const { authToken } = getState();
    const payload = {
      authToken,
      quoteId,
      deleteAuthHeader,
    };

    dispatch(finalizeQuoteRequest(quoteId));
    return new Promise((resolve, reject) => {
      api
        .finalizeQuote(payload)
        .then((finalize) => {
          dispatch(finalizeQuoteSuccess(finalize));
          resolve(finalize);
        })
        .catch((error) => {
          dispatch(finalizeQuoteFailure(error));
          reject(error);
        });
    });
  };
}
